import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable } from 'rxjs';
import { StatusPageService } from 'src/app/_theme/core/status-page/status-page.service';
import { isAppHost } from '../core/status-main-page.url-resolver';

type StatusEntityType = 'incident' | 'maintenance';
type StatusPageType = 'internal' | 'public';

@Component({
  selector: 'app-status-entity-redirect',
  standalone: true,
  template: ''
})
export class StatusEntityRedirectComponent implements OnInit {
  /**
   * Injects services used for routing, API calls, state management, and UI coordination.
   */
  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly statusPageService: StatusPageService
  ) {}

  /**
   * Initializes component state, derives route context, and starts first-pass data loading.
   */
  ngOnInit(): void {
    this.resolveShortLink();
  }

  /**
   * Resolves short link using fallback rules.
   */
  private resolveShortLink(): void {
    const entityType = this.getEntityType();
    const statusId = this.getStatusId();
    const entityId = this.getEntityId();
    if (!statusId || !entityId) {
      this.navigateNotFound();
      return;
    }

    this.getRedirectRequest(entityType, statusId, entityId).subscribe({
      next: (response: any) => {
        this.handleRedirectResponse(entityType, response);
      },
      error: () => {
        this.navigateNotFound();
      }
    });
  }

  /**
   * Handles redirect response and updates state accordingly.
   */
  private handleRedirectResponse(entityType: StatusEntityType, response: any): void {
    const redirectUrl = this.getRedirectUrl(response);
    if (redirectUrl) {
      this.redirectToResolvedUrl(redirectUrl);
      return;
    }

    const pageType = this.getStatusPageType(response?.type);
    const org = String(
      response?.org ||
      response?.organizationSlug ||
      response?.organization_slug ||
      ''
    ).trim();
    const slug = String(response?.slug || response?.pageSlug || response?.page_slug || '').trim();
    const resolvedEntityId = this.getResponseEntityId(response);
    if (pageType && org && slug && resolvedEntityId) {
      if (pageType === 'internal') {
        this.router.navigate(
          this.buildInternalStatusPageRoute(org, slug),
          {
            replaceUrl: true,
            queryParams: {
              theme: 'light'
            }
          }
        );
      } else {
        this.router.navigate(
          this.buildPublicFocusedEntityRoute(org, slug, entityType, resolvedEntityId),
          { replaceUrl: true }
        );
      }
      return;
    }

    this.navigateNotFound();
  }

  /**
   * Returns redirect request from the current state.
   */
  private getRedirectRequest(entityType: StatusEntityType, statusId: string, entityId: string): Observable<any> {
    if (entityType === 'maintenance') {
      return this.statusPageService.getPublicMaintenanceRedirect(statusId, entityId);
    }
    return this.statusPageService.getPublicIncidentRedirect(statusId, entityId);
  }

  /**
   * Builds focused entity route for downstream calls.
   */
  private buildPublicFocusedEntityRoute(
    org: string,
    slug: string,
    entityType: StatusEntityType,
    entityId: string
  ): any[] {
    if (!isAppHost(this.getCurrentHost())) {
      return ['/', this.getPublicEntitySegment(entityType), entityId];
    }

    const route = [
      '/',
      org,
      'status-main-page'
    ];
    route.push(slug, this.getPublicEntitySegment(entityType), entityId);
    return route;
  }

  /**
   * Returns the public route segment for the target entity type.
   */
  private getPublicEntitySegment(entityType: StatusEntityType): 'incidents' | 'maintenance' {
    return entityType === 'maintenance' ? 'maintenance' : 'incidents';
  }

  /**
   * Builds internal status page route for downstream calls.
   */
  private buildInternalStatusPageRoute(org: string, slug: string): any[] {
    return [
      '/',
      org,
      'status-main-page',
      'internal',
      slug
    ];
  }

  /**
   * Resolves normalized status page type from API payloads.
   */
  private getStatusPageType(value: any): StatusPageType | '' {
    const type = String(value || '').trim().toLowerCase();
    if (type === 'internal' || type === 'public') {
      return type;
    }
    return '';
  }

  /**
   * Returns entity id from redirect response from API payloads.
   */
  private getResponseEntityId(response: any): string {
    return String(response?.entityId ?? response?.entity_id ?? '').trim();
  }

  /**
   * Returns redirect url from redirect response from API payloads.
   */
  private getRedirectUrl(response: any): string {
    return String(response?.redirectUrl ?? response?.redirect_url ?? '').trim();
  }

  /**
   * Redirects using API-provided destination with SPA navigation for same-origin app routes.
   */
  private redirectToResolvedUrl(redirectUrl: string): void {
    const win = this.getWindow();
    if (!win?.location) {
      this.router.navigateByUrl(redirectUrl, { replaceUrl: true });
      return;
    }

    try {
      const resolvedUrl = new URL(redirectUrl, win.location.origin || win.location.href);
      if (resolvedUrl.origin === win.location.origin) {
        this.router.navigateByUrl(
          `${resolvedUrl.pathname}${resolvedUrl.search}${resolvedUrl.hash}`,
          { replaceUrl: true }
        );
        return;
      }

      win.location.replace(resolvedUrl.toString());
    } catch {
      this.router.navigateByUrl(redirectUrl, { replaceUrl: true });
    }
  }

  /**
   * Returns status page id from the current state.
   */
  private getStatusId(): string {
    return String(this.route.snapshot.paramMap.get('statusId') || '').trim();
  }

  /**
   * Returns entity type from the current state.
   */
  private getEntityType(): StatusEntityType {
    const entityType = String(this.route.snapshot.data?.['entityType'] || '').trim().toLowerCase();
    return entityType === 'maintenance' ? 'maintenance' : 'incident';
  }

  /**
   * Returns entity id from the current state.
   */
  private getEntityId(): string {
    const paramName = this.getEntityType() === 'maintenance' ? 'maintenanceId' : 'incidentId';
    return String(this.route.snapshot.paramMap.get(paramName) || '').trim();
  }

  /**
   * Returns window from the current runtime when available.
   */
  private getWindow(): Window | null {
    if (typeof globalThis === 'undefined') {
      return null;
    }
    return (globalThis as { window?: Window }).window ?? null;
  }

  /**
   * Returns current browser host when available.
   */
  private getCurrentHost(): string {
    const win = this.getWindow();
    return String(win?.location?.hostname || '').toLowerCase().trim();
  }

  /**
   * Navigates not found when prerequisites are missing.
   */
  private navigateNotFound(): void {
    this.router.navigate(['/error/404'], { replaceUrl: true });
  }
}
